import * as React from "react";
import { cn } from "@/lib/utils";

type Props = {
  className?: string;
  /** Show the sparkles particle layer on top of the aurora. */
  sparkles?: boolean;
};

/**
 * Aurora background — three layered diagonal beams of gold/peach/cyan
 * that drift sideways with subtle scale + opacity breathing. Combined
 * with a faint sparkle field, it's the "richer" hero backdrop you
 * asked for.
 *
 * Render INSIDE a `relative` parent and let it absolute-fill. Pointer
 * events disabled so clicks pass through.
 */
export function AuroraBackground({ className, sparkles = true }: Props) {
  return (
    <div aria-hidden className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
      {/* Deep base wash. */}
      <div className="absolute inset-0 bg-gradient-to-b from-bg via-bg to-bg-2" />

      {/* Aurora beams. Each is a wide, blurred diagonal stripe that
          slowly drifts horizontally + breathes opacity. */}
      <div className="absolute -inset-[20%] [filter:blur(80px)] opacity-90">
        <div className="absolute top-[10%] left-[-20%] w-[140%] h-[40%] rotate-[12deg]
                        bg-gradient-to-r from-transparent via-[rgba(201,169,106,0.35)] to-transparent
                        animate-[aurora1_18s_ease-in-out_infinite]" />
        <div className="absolute top-[35%] left-[-30%] w-[160%] h-[35%] -rotate-[8deg]
                        bg-gradient-to-r from-transparent via-[rgba(224,195,136,0.28)] to-transparent
                        animate-[aurora2_22s_ease-in-out_infinite]" />
        <div className="absolute top-[55%] left-[-25%] w-[150%] h-[45%] rotate-[18deg]
                        bg-gradient-to-r from-transparent via-[rgba(217,119,87,0.22)] to-transparent
                        animate-[aurora3_26s_ease-in-out_infinite]" />
        <div className="absolute top-[20%] left-[-10%] w-[120%] h-[55%] -rotate-[14deg]
                        bg-gradient-to-r from-transparent via-[rgba(56,189,248,0.10)] to-transparent
                        animate-[aurora4_30s_ease-in-out_infinite]" />
      </div>

      {/* Top radial highlight — keeps the focus toward the hero. */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[600px]"
        style={{ background: "radial-gradient(ellipse at top, rgba(201,169,106,0.18), transparent 60%)" }} />

      {/* Vignette darken at edges. */}
      <div className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse at center, transparent 50%, rgba(10,10,11,0.45) 100%)" }} />

      {sparkles && <SparklesLayer />}

      <style>{`
        @keyframes aurora1 { 0%,100% { transform: translateX(-6%) scale(1)   } 50% { transform: translateX(8%)  scale(1.05) } }
        @keyframes aurora2 { 0%,100% { transform: translateX(6%)  scale(1)   } 50% { transform: translateX(-7%) scale(1.08) } }
        @keyframes aurora3 { 0%,100% { transform: translateX(-4%) scale(1.02) } 50% { transform: translateX(10%) scale(0.98) } }
        @keyframes aurora4 { 0%,100% { transform: translateX(2%)  scale(1)   } 50% { transform: translateX(-5%) scale(1.06) } }
      `}</style>
    </div>
  );
}

/* ───────────────── Sparkles ───────────────── */

function SparklesLayer() {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    let width = 0, height = 0;

    type P = { x: number; y: number; r: number; a: number; vx: number; vy: number; tw: number };
    let particles: P[] = [];

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      width = r.width; height = r.height;
      canvas.width  = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      particles = Array.from({ length: 100 }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: 0.5 + Math.random() * 1.6,
        a: 0.2 + Math.random() * 0.6,
        vx: (Math.random() - 0.5) * 0.12,
        vy: -0.04 - Math.random() * 0.12,
        tw: Math.random() * Math.PI * 2
      }));
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let raf = 0;
    let t = 0;
    const draw = () => {
      t += reduced ? 0 : 1;
      ctx.clearRect(0, 0, width, height);
      for (const p of particles) {
        if (!reduced) {
          p.x += p.vx;
          p.y += p.vy;
          p.tw += 0.03;
        }
        if (p.y < -10) { p.y = height + 10; p.x = Math.random() * width; }
        if (p.x < -10 || p.x > width + 10) { p.x = Math.random() * width; }
        const flicker = 0.6 + 0.4 * Math.sin(p.tw);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(224,195,136,${(p.a * flicker).toFixed(3)})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [reduced]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />;
}

function useReducedMotion() {
  const [v, setV] = React.useState(false);
  React.useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setV(m.matches);
    const h = () => setV(m.matches);
    m.addEventListener("change", h);
    return () => m.removeEventListener("change", h);
  }, []);
  return v;
}
